import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod/v4';
import { useTranslation } from 'react-i18next';
import { getApiErrorDetail } from '@klareospace/pharos-lib-front-utils';

import type { Carrier } from '@klareospace/pharos-lib-types';

import { useUpdateCarrier } from './use-update-carrier';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface GeneralFormInput {
  code: string;
  name: string;
}

export interface UseCarrierGeneralFormReturn {
  form: ReturnType<typeof useForm<GeneralFormInput>>;
  isDirty: boolean;
  isPending: boolean;
  onReset: () => void;
  onSubmit: (data: GeneralFormInput) => void;
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useCarrierGeneralForm(carrier: Carrier): UseCarrierGeneralFormReturn {
  const { t } = useTranslation('settings');
  const updateCarrier = useUpdateCarrier();

  const schema = useMemo(
    () =>
      z.object({
        code: z.string().trim().min(1, t('common.carrierCodeRequired')),
        name: z.string().trim().min(1, t('common.carrierNameRequired')),
      }),
    [t],
  );

  const form = useForm<GeneralFormInput>({
    defaultValues: { code: carrier.code, name: carrier.name },
    mode: 'onChange',
    resolver: zodResolver(schema),
  });

  function onReset(): void {
    form.reset({ code: carrier.code, name: carrier.name });
  }

  function onSubmit(data: GeneralFormInput): void {
    updateCarrier.mutateAsync({ data: { code: data.code, name: data.name }, id: carrier.id }).then(
      () => {
        form.reset(data);
      },
      (error: unknown) => {
        const detail = getApiErrorDetail(error);
        if (detail.statusCode === 409) {
          const message = detail.message ?? '';
          if (message.toLowerCase().includes('code')) {
            form.setError('code', { message: t('common.errorDuplicateCode') });
          } else {
            form.setError('name', { message: t('common.errorDuplicateName') });
          }
          return;
        }
      },
    );
  }

  return {
    form,
    isDirty: form.formState.isDirty,
    isPending: updateCarrier.isPending,
    onReset,
    onSubmit,
  };
}
